import { eachDay, format, getHours, getMinutes } from "date-fns";
import {
  any,
  filter,
  find,
  flatten,
  isNil,
  map,
  propEq,
  reject,
  sort,
  uniqBy,
} from "ramda";
import { Maybe } from "true-myth";
import { default as makeUuid } from "uuid/v4";

import { INewShift } from "actions/scheduling/blocks";
import { IOption } from "components/shared/dropdown";
import { ITabButton } from "components/shared/tablist";
import {
  IFormValues,
  ITimeBlockFormErrors,
  DEFAULT_ERRORS,
} from "components/schedule/time_block_form";
import { anyValue } from "helpers/collections";
import { dateToWeekday, sixDaysLater, DateFormats } from "helpers/dates";
import { isAllDay } from "helpers/times";
import {
  formatTimeBlock,
  findPersonTimeConflict,
  isBlockUnavail,
  TimeConflictMessages,
} from "helpers/schedule/shifts";
import {
  Block,
  Frequency,
  IPeople,
  IPerson,
  IPosUser,
  IRole,
  IShift,
  IUnavailabilityBlock,
  UnavailType,
} from "types/schedules";

type ExistingBlock = Maybe<Block>;

type IRoleDropdownOption = IOption<Maybe<IRole>>;
type IEmployeeDropdownOption = IOption<Maybe<IPerson>>;
type ITimeDropdownOption = IOption<number>;
type IFrequencyDropdownOption = IOption<Frequency>;

enum TOGGLE_MODES {
  SHIFT = "shift",
  UNAVAILABLE = "unavailable",
}
const TAB_BUTTONS: ITabButton[] = [
  { children: "Shift", id: TOGGLE_MODES.SHIFT },
  { children: "Unavailable", id: TOGGLE_MODES.UNAVAILABLE },
];

const EMPLOYEE_DEFAULT: IEmployeeDropdownOption = {
  id: "",
  label: "Select an Employee",
  meta: Maybe.nothing(),
};

const ROLE_DEFAULT: IRoleDropdownOption = {
  id: "",
  label: "Select a Role",
  meta: Maybe.nothing(),
};

const ONCE: IFrequencyDropdownOption = {
  id: "once",
  label: "Does not repeat",
  meta: Frequency.ONCE,
};
const WEEKLY: IFrequencyDropdownOption = {
  id: "weekly",
  label: "Weekly",
  meta: Frequency.WEEKLY,
};
function getFrequencyOptions(date: Date): IFrequencyDropdownOption[] {
  // Make a fresh set of options which specifies day of week in label
  const weeklyLabel = `Weekly on ${dateToWeekday(date)}`;
  return [ONCE, { ...WEEKLY, label: weeklyLabel }];
}

function isFrequencyRecurring(frequencyOpt: IFrequencyDropdownOption) {
  return frequencyOpt !== ONCE;
}

const SHIFT_MINUTES: ITimeDropdownOption[] = [
  { id: "00", label: "00", meta: 0 },
  { id: "15", label: "15", meta: 15 },
  { id: "30", label: "30", meta: 30 },
  { id: "45", label: "45", meta: 45 },
];

const MINUTE_DEFAULT: ITimeDropdownOption = SHIFT_MINUTES[0];

const SHIFT_HOURS: ITimeDropdownOption[] = [
  { id: "12 am", label: "12 am", meta: 0 },
  { id: "1 am", label: "1 am", meta: 1 },
  { id: "2 am", label: "2 am", meta: 2 },
  { id: "3 am", label: "3 am", meta: 3 },
  { id: "4 am", label: "4 am", meta: 4 },
  { id: "5 am", label: "5 am", meta: 5 },
  { id: "6 am", label: "6 am", meta: 6 },
  { id: "7 am", label: "7 am", meta: 7 },
  { id: "8 am", label: "8 am", meta: 8 },
  { id: "9 am", label: "9 am", meta: 9 },
  { id: "10 am", label: "10 am", meta: 10 },
  { id: "11 am", label: "11 am", meta: 11 },
  { id: "12 pm", label: "12 pm", meta: 12 },
  { id: "1 pm", label: "1 pm", meta: 13 },
  { id: "2 pm", label: "2 pm", meta: 14 },
  { id: "3 pm", label: "3 pm", meta: 15 },
  { id: "4 pm", label: "4 pm", meta: 16 },
  { id: "5 pm", label: "5 pm", meta: 17 },
  { id: "6 pm", label: "6 pm", meta: 18 },
  { id: "7 pm", label: "7 pm", meta: 19 },
  { id: "8 pm", label: "8 pm", meta: 20 },
  { id: "9 pm", label: "9 pm", meta: 21 },
  { id: "10 pm", label: "10 pm", meta: 22 },
  { id: "11 pm", label: "11 pm", meta: 23 },
];

const BLANK_TIME: ITimeDropdownOption = { id: "-", label: "-", meta: -1 };

const START_HOUR_DEFAULT: ITimeDropdownOption = SHIFT_HOURS[12];
const END_HOUR_DEFAULT: ITimeDropdownOption = SHIFT_HOURS[16];

function getRawTimes({
  allDay,
  date,
  startHour,
  startMinute,
  endHour,
  endMinute,
}: IFormValues) {
  return {
    date: date.meta,
    startHour: allDay ? 0 : startHour.meta,
    startMinute: allDay ? 0 : startMinute.meta,
    endHour: allDay ? 23 : endHour.meta,
    endMinute: allDay ? 59 : endMinute.meta,
  };
}

function sortDropdownOptions<T>(list: Array<IOption<T>>): Array<IOption<T>> {
  const uniqOpts = uniqBy(option => option.id, list);
  return sort((optionA: IOption<T>, optionB: IOption<T>) => {
    return optionA.label.localeCompare(optionB.label);
  }, uniqOpts);
}

function getEmployeeOptions(employees: IPeople): IEmployeeDropdownOption[] {
  const realPeople = filter((person: IPerson) => {
    return Maybe.isJust(person.assignee);
  }, employees);

  const formattedEmployess: Array<IEmployeeDropdownOption | null> = map(
    (employee: IPerson): IEmployeeDropdownOption | null => {
      return employee.assignee
        .map(
          (user: IPosUser): IEmployeeDropdownOption | null => {
            const { uuid, firstName, lastName } = user;
            const fullName = `${firstName} ${lastName}`;
            return { id: uuid, label: fullName, meta: Maybe.of(employee) };
          }
        )
        .unwrapOr(null);
    },
    realPeople
  );

  const realEmployees: IEmployeeDropdownOption[] = reject(
    isNil,
    formattedEmployess
  ) as IEmployeeDropdownOption[];

  return sortDropdownOptions<Maybe<IPerson>>(realEmployees);
}

function getEmployeeOptionsByRole(
  selectedRole: IRole,
  employees: IPeople
): IEmployeeDropdownOption[] {
  const employeesWithRole: IPeople = filter(employee => {
    return employee.assignee
      .map(emp => {
        return any(role => selectedRole.uuid === role.uuid, emp.roles);
      })
      .unwrapOr(false);
  }, employees);
  return getEmployeeOptions(employeesWithRole);
}

function getEmployeeRoles(assignee: Maybe<IPosUser>): IRoleDropdownOption[] {
  const roles = assignee
    .map(employee =>
      map(
        role => ({ id: role.uuid, label: role.name, meta: Maybe.of(role) }),
        employee.roles
      )
    )
    .unwrapOr([]);

  return sortDropdownOptions<Maybe<IRole>>(roles);
}

function getAllRoles(people: IPeople): IRoleDropdownOption[] {
  const allRoles = flatten<IRoleDropdownOption>(
    map((person: IPerson) => getEmployeeRoles(person.assignee), people)
  );

  return sortDropdownOptions<Maybe<IRole>>(allRoles);
}

function getScheduleDates(
  startDate: Maybe<Date>,
  endDate: Maybe<Date>
): Array<IOption<Date>> {
  return startDate
    .map((start: Date) => {
      return endDate.match({
        Just: (end: Date) => {
          const weekOfDates = eachDay(start, end);
          return map(
            date => ({
              id: format(date, DateFormats.WEEKDAY_FULL),
              label: format(date, DateFormats.WEEKDAY_FULL),
              meta: date,
            }),
            weekOfDates
          );
        },
        Nothing: () => {
          const end: Date = sixDaysLater(start);
          const weekOfDates = eachDay(start, end);
          return map(
            date => ({
              id: format(date, DateFormats.WEEKDAY_FULL),
              label: format(date, DateFormats.WEEKDAY_FULL),
              meta: date,
            }),
            weekOfDates
          );
        },
      });
    })
    .unwrapOr([]);
}

function getInitialDateOption(
  date: Date,
  dateOptions: Array<IOption<Date>>
): IOption<Date> {
  const foundDate = Maybe.of(
    find(propEq("label", format(date, DateFormats.WEEKDAY_FULL)), dateOptions)
  );
  return foundDate.unwrapOr(dateOptions[0]);
}

function getInitialEmployeeOption(
  employee: Maybe<IPosUser>,
  employeeOptions: IEmployeeDropdownOption[]
): IEmployeeDropdownOption {
  return employee.match({
    Just: assignee => {
      const foundEmployeeOption = Maybe.of(
        find(propEq("id", assignee.uuid), employeeOptions)
      );
      return foundEmployeeOption.unwrapOr(EMPLOYEE_DEFAULT);
    },
    Nothing: () => EMPLOYEE_DEFAULT,
  });
}

function getInitialRoleOption(
  employee: Maybe<IPosUser>,
  existingBlock: ExistingBlock,
  allRoles: IRoleDropdownOption[]
): IRoleDropdownOption {
  return existingBlock.match({
    Just: block => {
      const shiftRole = Maybe.of(
        isBlockUnavail(block)
          ? null // unavails don't have Role
          : find(propEq("id", (block as IShift).role.uuid), allRoles)
      );

      return shiftRole.unwrapOr(ROLE_DEFAULT);
    },
    Nothing: () => {
      const employeeRoles = getEmployeeRoles(employee);

      if (employeeRoles.length === 1) {
        return employeeRoles[0];
      } else {
        return ROLE_DEFAULT;
      }
    },
  });
}

function getOptionForTimeField(
  block: Block,
  isStart: boolean,
  position: "hour" | "minute"
) {
  const isHour = position === "hour";
  const options = isHour ? SHIFT_HOURS : SHIFT_MINUTES;
  const defaultValue = isHour
    ? isStart
      ? START_HOUR_DEFAULT
      : END_HOUR_DEFAULT
    : MINUTE_DEFAULT;

  const fieldName = isStart ? "startTime" : "endTime";
  const getNumber = isHour ? getHours : getMinutes;
  const matchNumber = getNumber(block[fieldName]);
  const timeOption = Maybe.of(find(propEq("meta", matchNumber), options));
  return timeOption.unwrapOr(defaultValue);
}

function getInitialTimeOptions(
  existingBlock: ExistingBlock,
  type: "start" | "end",
  defaultHour: number
): ITimeDropdownOption[] {
  const isStart = type === "start";

  return existingBlock.match({
    Just: shiftUnavail => [
      getOptionForTimeField(shiftUnavail, isStart, "hour"),
      getOptionForTimeField(shiftUnavail, isStart, "minute"),
    ],
    Nothing: () => {
      return [
        isStart
          ? SHIFT_HOURS[defaultHour % SHIFT_HOURS.length] || START_HOUR_DEFAULT
          : SHIFT_HOURS[(defaultHour + 7) % SHIFT_HOURS.length] ||
            END_HOUR_DEFAULT,
        MINUTE_DEFAULT,
      ];
    },
  });
}

interface IInitialValuesRequire {
  allEmployeeOptions: IEmployeeDropdownOption[];
  allRoleOptions: IRoleDropdownOption[];
  date: Date;
  defaultHour: number;
  existingBlock: ExistingBlock;
  person: IPerson;
  scheduleDates: Array<IOption<Date>>;
}

function getInitialValues({
  allEmployeeOptions,
  allRoleOptions,
  date,
  defaultHour,
  existingBlock,
  person,
  scheduleDates,
}: IInitialValuesRequire): IFormValues {
  const dateInit = getInitialDateOption(date, scheduleDates);
  const employee = getInitialEmployeeOption(
    person.assignee,
    allEmployeeOptions
  );
  const role = getInitialRoleOption(
    person.assignee,
    existingBlock,
    allRoleOptions
  );
  const [startHour, startMinute] = getInitialTimeOptions(
    existingBlock,
    "start",
    defaultHour
  ); // Default: 12:00pm
  const [endHour, endMinute] = getInitialTimeOptions(
    existingBlock,
    "end",
    defaultHour
  ); // Default: 4:00pm

  // Check existingBlock for the unavail-specific fields
  const allDay = existingBlock.match({
    Just: block => isAllDay(block.startTime, block.endTime),
    Nothing: () => false,
  });
  const frequencyValue = existingBlock.match({
    Just: block =>
      isBlockUnavail(block)
        ? (block as IUnavailabilityBlock).frequency
        : Frequency.ONCE,
    Nothing: () => Frequency.ONCE,
  });
  const frequency = frequencyValue === Frequency.WEEKLY ? WEEKLY : ONCE;

  return {
    allDay,
    date: dateInit,
    employee,
    endHour,
    endMinute,
    frequency,
    role,
    startHour,
    startMinute,
  };
}

function validateFormValues(
  isUnavail: boolean,
  existingBlock: ExistingBlock,
  formValues: IFormValues
): [ITimeBlockFormErrors, boolean] {
  let errors = DEFAULT_ERRORS;
  let canOverride = false;

  if (isUnavail) {
    // Unavailable requires employee to be selected
    if (!employeeSelected(formValues.employee)) {
      errors = {
        ...DEFAULT_ERRORS,
        employee: "Select an employee to continue.",
      };
    }
  } else if (!roleSelected(formValues.role)) {
    // Shift requires role to be selected
    errors = {
      ...DEFAULT_ERRORS,
      role: "Select a role to continue",
    };
  }

  // If there are no errors so far, check for time block conflicts
  // @ts-expect-error TODO please fix this error
  if (!anyValue(errors)) {
    const timeBlock = formatTimeBlock(getRawTimes(formValues));
    // Only look for conflicts if an employee is selected (Unassigned row can't have conflicts)
    const conflictInfo = formValues.employee.meta.match({
      Just: selectedEmployee =>
        findPersonTimeConflict(
          selectedEmployee,
          timeBlock,
          existingBlock,
          isUnavail
        ),
      Nothing: () => ({
        conflict: TimeConflictMessages.NONE,
        canOverride: false,
      }),
    });
    if (conflictInfo.conflict) {
      errors = {
        ...DEFAULT_ERRORS,
        conflict: conflictInfo.conflict,
      };
      canOverride = conflictInfo.canOverride;
    }
  }
  return [errors, canOverride];
}

function newUnavailFromFormValues(
  formValues: IFormValues,
  existingBlock: ExistingBlock
): Maybe<IUnavailabilityBlock> {
  const { employee, frequency } = formValues;
  return employee.meta.map(selectedEmployee => {
    const uuid = existingBlock.match({
      Just: block => (block as IUnavailabilityBlock).uuid,
      Nothing: () => "",
    });
    const rawTimes = getRawTimes(formValues);
    const timeBlock = formatTimeBlock(rawTimes);
    const isRecurring = isFrequencyRecurring(formValues.frequency);
    return {
      ...timeBlock,
      date: rawTimes.date,
      dayOfWeek: isRecurring ? dateToWeekday(rawTimes.date) : undefined,
      frequency: frequency.meta,
      type: "UNAVAILABLE" as UnavailType,
      userId: selectedEmployee.assignee
        .map(selectedAssignee => selectedAssignee.uuid)
        .unwrapOr(""),
      uuid,
    };
  });
}

function newShiftFromFormValues(
  formValues: IFormValues,
  existingBlock: ExistingBlock
): Maybe<INewShift> {
  const { employee, role } = formValues;
  return role.meta.map(selectedRole => {
    const newShiftAssignee = employee.meta.andThen(
      employeePerson => employeePerson.assignee
    );
    // Find the role which matches selectedRole, for this particular employee
    const employeeRole = newShiftAssignee.match({
      Just: assignee => {
        const foundRole = assignee.roles.find(
          propEq("uuid", selectedRole.uuid)
        );
        return foundRole || selectedRole;
      },
      Nothing: () => selectedRole,
    });
    const rawTimes = getRawTimes(formValues);
    return {
      ...rawTimes,
      employee: newShiftAssignee,
      role: employeeRole,
      uuid: existingBlock.map(shift => shift.uuid).unwrapOr(makeUuid()),
    };
  });
}

function employeeSelected(employee: IEmployeeDropdownOption): boolean {
  return employee.id !== EMPLOYEE_DEFAULT.id;
}

function roleSelected(role: IRoleDropdownOption): boolean {
  return role.id !== ROLE_DEFAULT.id;
}

export {
  BLANK_TIME,
  EMPLOYEE_DEFAULT,
  employeeSelected,
  END_HOUR_DEFAULT,
  ExistingBlock,
  getAllRoles,
  getEmployeeOptions,
  getEmployeeOptionsByRole,
  getEmployeeRoles,
  getFrequencyOptions,
  getInitialValues,
  getRawTimes,
  getScheduleDates,
  isFrequencyRecurring,
  newUnavailFromFormValues,
  newShiftFromFormValues,
  IEmployeeDropdownOption,
  IFrequencyDropdownOption,
  IInitialValuesRequire,
  IRoleDropdownOption,
  ITimeDropdownOption,
  ROLE_DEFAULT,
  roleSelected,
  validateFormValues,
  ONCE,
  SHIFT_HOURS,
  SHIFT_MINUTES,
  START_HOUR_DEFAULT,
  MINUTE_DEFAULT,
  TOGGLE_MODES,
  TAB_BUTTONS,
};
